/**
 * Making a table synced with a spreadsheet in Lithos Assets.
 *
 * Reached from Assets with the file in the address. The person picks one of its
 * sheets laid out as a table, and is told what syncing does to their file before
 * they agree to it: a hidden `_lithos_row_id` column is added to the sheet, which
 * is how its rows are told apart once edits flow both ways.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/errors'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'
import { Skeleton } from '@/shared/ui/skeleton'
import { useAssetsSheets, useCreateSpreadsheetSync } from './queries'

/** The detail a sync error carries is the reason, written for the person. */
function reason(error: unknown): string | null {
  return error instanceof ApiError && typeof error.detail === 'string'
    ? error.detail
    : null
}

export function CreateSpreadsheetSyncDialog({
  databaseId,
  fileId,
  onClose,
  onCreated,
}: {
  databaseId: number
  fileId: number
  onClose: () => void
  onCreated: (tableId: number) => void
}) {
  const { t } = useTranslation()
  const sheets = useAssetsSheets(databaseId, fileId)
  const create = useCreateSpreadsheetSync(databaseId)

  const tables = sheets.data?.sheets.filter((sheet) => sheet.tabular) ?? []
  const [chosen, setChosen] = useState<string | null>(null)
  const [name, setName] = useState<string | null>(null)
  const sheet = chosen ?? tables[0]?.name ?? ''
  const tableName = name ?? sheet

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('dataSync.assets.title')}</DialogTitle>
          <DialogDescription>
            {sheets.data
              ? t('dataSync.assets.description', { file: sheets.data.file_name })
              : t('dataSync.assets.reading')}
          </DialogDescription>
        </DialogHeader>

        {sheets.isPending ? (
          <Skeleton className="h-24" />
        ) : sheets.isError ? (
          <p className="text-sm text-destructive">
            {reason(sheets.error) ?? t('dataSync.assets.unreadable')}
          </p>
        ) : tables.length === 0 ? (
          <p className="text-sm text-destructive">{t('dataSync.assets.noTables')}</p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-1">
              <Label htmlFor="assets-sheet" className="text-xs text-muted-foreground">
                {t('dataSync.settings.sheet_name')}
              </Label>
              <NativeSelect
                id="assets-sheet"
                value={sheet}
                onChange={(event) => setChosen(event.target.value)}
              >
                {tables.map((option) => (
                  <option key={option.name} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-1">
              <Label
                htmlFor="assets-table-name"
                className="text-xs text-muted-foreground"
              >
                {t('dataSync.assets.tableName')}
              </Label>
              <Input
                id="assets-table-name"
                value={tableName}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
              {t('dataSync.assets.rowIdNotice')}
            </p>
            {create.isError && (
              <p className="text-sm text-destructive">
                {reason(create.error) ?? t('dataSync.assets.createFailed')}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            disabled={tables.length === 0 || !tableName.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { fileId, sheetName: sheet, tableName: tableName.trim() },
                { onSuccess: (table) => onCreated(table.id) },
              )
            }
          >
            {t('dataSync.assets.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
